import { useCallback, useEffect, useState } from "react";
import {
  Card,
  Text,
  BlockStack,
  Spinner,
  Divider,
  Select,
  Box,
  Button
} from "@shopify/polaris";
import { useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchAuditLogs } from "services/audit-logs.service";
import { useSelector } from "react-redux";
import { RootState } from "configureStore";
import { AuditLogEntry } from "components/audit-log/audit-log-entry";
import type { AuditLogRow } from "components/audit-log/auditLogTypes";
import PaginationControls from "components/pagination/PaginationControls";
import { AuditLogUserSearchField } from "components/audit-log/audit-log-user-search-field";
import {
  AUDIT_SUBJECT_FILTER_TYPES,
  getSubjectTypeLabel,
} from "components/audit-log/audit-subject-registry";
import { Activity, SlidersHorizontal, RotateCcw, User, Eye } from "lucide-react";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export default function AuditLogSettingsContainer() {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const { role } = useSelector((state: RootState) => state.authReducer);
  const isMaster =
    !!role?.isMaster || !!role?.permissionsgroups?.includes("Master");
  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ subjectType: "", userId: "" });
  const [selectedUserName, setSelectedUserName] = useState("");
  const [paginationState, setPaginationState] = useState({
    page: 1,
    pageSize: 20,
  });

  const { page, pageSize } = paginationState;
  const limit = pageSize;
  const skip = (page - 1) * pageSize;

  const runFetch = useCallback(() => {
    if (!companyId || !isMaster) {
      return;
    }
    setLoading(true);
    fetchAuditLogs(companyId, {
      limit,
      skip,
      subjectType: filters.subjectType || undefined,
      userId: filters.userId.trim() || undefined,
    })
      .then((data) => {
        setRows((data.rows || []) as AuditLogRow[]);
        setTotal(typeof data.count === "number" ? data.count : 0);
        setError(false);
      })
      .catch(() => {
        setError(true);
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [companyId, isMaster, limit, skip, filters.subjectType, filters.userId]);

  useEffect(() => {
    runFetch();
  }, [runFetch]);

  if (!isMaster) {
    return <Navigate to={`/app/${companyId}/settings`} replace />;
  }

  if (!companyId) {
    return null;
  }

  const handleFilterChange = (key: "subjectType" | "userId", value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({ subjectType: "", userId: "" });
    setSelectedUserName("");
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  };

  const totalPages =
    total > 0 ? Math.ceil(total / pageSize) : 0;
  const hasNextPage = total > 0 && page * pageSize < total;
  const hasPreviousPage = page > 1;

  const handlePageChange = (nextPage: number) => {
    if (nextPage > 0 && nextPage !== page) {
      setPaginationState((prev) => ({ ...prev, page: nextPage }));
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    if (nextPageSize > 0 && nextPageSize !== pageSize) {
      setPaginationState((prev) => ({ ...prev, page: 1, pageSize: nextPageSize }));
    }
  };

  const subjectOptions = [
    { label: t("all"), value: "" },
    ...AUDIT_SUBJECT_FILTER_TYPES.map((subjectType) => ({
      label: getSubjectTypeLabel(t, subjectType),
      value: subjectType,
    })),
  ];

  const hasActiveFilters = !!(filters.subjectType || filters.userId);

  return (
    <div>
      {/* Stats Overview */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4 transition duration-200 hover:shadow-md">
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
            <Activity size={24} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t("audit_log_total")}</p>
            <h3 className="text-2xl font-bold text-slate-800 mt-0.5">{total}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4 transition duration-200 hover:shadow-md">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
            <Eye size={24} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t("audit_log_active_action")}</p>
            <h3 className="text-base font-semibold text-slate-700 mt-1 truncate max-w-[180px]">
              {filters.subjectType ? getSubjectTypeLabel(t, filters.subjectType) : t("audit_log_all_actions")}
            </h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4 transition duration-200 hover:shadow-md">
          <div className="p-3 bg-sky-50 text-sky-600 rounded-xl">
            <User size={24} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t("audit_log_active_user")}</p>
            <h3 className="text-base font-semibold text-slate-700 mt-1 truncate max-w-[180px]">
              {filters.userId ? (selectedUserName || filters.userId) : t("audit_log_all_users")}
            </h3>
          </div>
        </div>
      </div>

      <BlockStack gap="400">
        <Card>
          <BlockStack gap="500">
            {/* Search & Filter bar - Pixel-perfect Alignment */}
            <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-4 p-1">
              <div className="flex-1 grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <span className="flex items-center gap-1.5 text-slate-600 font-semibold text-sm h-6">
                    <SlidersHorizontal size={14} className="text-slate-400" />
                    <span>{t("action")}</span>
                  </span>
                  <Select
                    label=""
                    labelHidden
                    options={subjectOptions}
                    value={filters.subjectType}
                    onChange={(v) => handleFilterChange("subjectType", v)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="flex items-center gap-1.5 text-slate-600 font-semibold text-sm h-6">
                    <User size={14} className="text-slate-400" />
                    <span>{t("performed_by")}</span>
                  </span>
                  <AuditLogUserSearchField
                    value={filters.userId}
                    onChange={(v) => handleFilterChange("userId", v)}
                    onUserNameResolved={setSelectedUserName}
                    label=""
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 min-h-9 items-center self-start xl:self-end">
                {hasActiveFilters && (
                  <Button
                    icon={<RotateCcw className="w-4 h-4" />}
                    onClick={handleResetFilters}
                    variant="secondary"
                  >
                    {t("audit_log_clear_filters")}
                  </Button>
                )}
                <Button
                  icon={<Activity className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />}
                  onClick={runFetch}
                  disabled={loading}
                  variant="secondary"
                >
                  {t("audit_log_refresh")}
                </Button>
              </div>
            </div>

            <Divider />

            {/* Log list / feed */}
            {loading ? (
              <div className="py-12 flex flex-col items-center justify-center gap-3">
                <Spinner size="large" />
                <p className="text-slate-400 text-sm animate-pulse">{t("audit_log_loading")}</p>
              </div>
            ) : error ? (
              <Box padding="400">
                <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-center">
                  <Text as="p" tone="critical" fontWeight="medium">
                    {t("audit_log_load_failed")}
                  </Text>
                </div>
              </Box>
            ) : (
              <div className="flow-root">
                {rows.length === 0 ? (
                  <div className="text-center py-16 px-4 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                    <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium text-lg">
                      {t("audit_log_empty")}
                    </p>
                    <p className="text-slate-400 text-sm mt-1 max-w-md mx-auto">
                      {hasActiveFilters 
                        ? t("audit_log_empty_filtered_hint")
                        : t("audit_log_empty_hint")}
                    </p>
                    {hasActiveFilters && (
                      <div className="mt-4">
                        <Button size="slim" onClick={handleResetFilters}>
                          {t("audit_log_clear_filters")}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative sm:pl-6 before:absolute before:top-2 before:bottom-2 before:left-[11px] before:w-[2px] before:bg-slate-100 max-sm:before:hidden">
                    {rows.map((row, i) => (
                      <div key={row.id} className="relative mb-6 last:mb-0 group">
                        {/* Dot indicator on timeline */}
                        <span className="max-sm:hidden absolute -left-[20px] top-3.5 z-10 w-2.5 h-2.5 rounded-full bg-slate-300 border-2 border-white ring-4 ring-white group-hover:scale-125 transition duration-200" />
                        <div className="p-3 sm:p-4 bg-white hover:bg-slate-50/60 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition duration-200">
                          <AuditLogEntry
                            companyId={companyId}
                            row={row}
                            showSubjectLine
                            showOpenLink={true}
                            showProvenance={true}
                            padded={false}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {total > 0 && !loading && (
                  <div className="mt-6 border-t border-slate-100 pt-6">
                    <PaginationControls
                      page={page}
                      pageSize={pageSize}
                      total={total}
                      totalPages={totalPages}
                      hasNextPage={hasNextPage}
                      hasPreviousPage={hasPreviousPage}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      pageSizeOptions={PAGE_SIZE_OPTIONS}
                      isLoading={false}
                    />
                  </div>
                )}
              </div>
            )}
          </BlockStack>
        </Card>
      </BlockStack>
    </div>
  );
}
